<template>
  <section class="ebook-hero-section">
    <div class="hero-container">
      <div class="hero-content">
        <h1 class="hero-title">{{ $t('ebook.hero.title') }}</h1>
        <p class="hero-subtitle">{{ $t('ebook.hero.subtitle') }}</p>

        <!-- 电子书三件套展示 -->
        <div class="format-showcase">
          <div class="format-item">
            <Icon name="material-symbols:menu-book" size="24" />
            <span>{{ $t('ebook.hero.icons.epub') }}</span>
          </div>
          <div class="format-item">
            <Icon name="material-symbols:devices" size="24" />
            <span>{{ $t('ebook.hero.icons.mobi') }}</span>
          </div>
          <div class="format-item">
            <Icon name="material-symbols:import-contacts" size="24" />
            <span>{{ $t('ebook.hero.icons.azw') }}</span>
          </div>
        </div>

        <!-- 特色标签 -->
        <div class="feature-tags">
          <div class="feature-tag">
            <Icon name="material-symbols:auto-stories" size="16" />
            <span>{{ $t('ebook.hero.features.reading') }}</span>
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:menu-book" size="16" />
            <span>{{ $t('ebook.hero.features.chapters') }}</span>
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:speed" size="16" />
            <span>{{ $t('ebook.hero.features.fast') }}</span>
          </div>
          <div class="feature-tag">
            <Icon name="material-symbols:support-agent" size="16" />
            <span>{{ $t('ebook.hero.features.support') }}</span>
          </div>
        </div>
      </div>

      <!-- Hero背景装饰 -->
      <div class="hero-decoration">
        <div class="decoration-circle decoration-1"></div>
        <div class="decoration-circle decoration-2"></div>
        <div class="decoration-circle decoration-3"></div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
// 无需额外逻辑，纯展示组件
</script>

<style scoped lang="scss">
@use 'sass:color';

.ebook-hero-section {
  background:
    linear-gradient(135deg,
      rgba(#FF8C42, 0.8) 0%,
      rgba(#4A90E2, 0.8) 25%,
      rgba(#52C41A, 0.8) 50%,
      rgba(#4A90E2, 0.8) 75%,
      rgba(#FF8C42, 0.8) 100%),
    url('/images/ebook/hero-background.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: $spacing-xxl 0;
  position: relative;
  overflow: hidden;
  min-height: 500px;
  display: flex;
  align-items: center;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 $spacing-lg;
  position: relative;
  z-index: 2;
  width: 100%;
}

.hero-content {
  text-align: center;
  color: $white;
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 3.2rem;
  font-weight: 800;
  margin: 0 0 $spacing-lg 0;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba($black, 0.3);

  background: linear-gradient(135deg,
    rgba($white, 1) 0%,
    rgba($white, 0.9) 50%,
    rgba($white, 1) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.4rem;
  margin: 0 0 $spacing-xl 0;
  opacity: 0.95;
  line-height: 1.5;
  text-shadow: 0 1px 4px rgba($black, 0.2);
}

.format-showcase {
  display: flex;
  justify-content: center;
  gap: $spacing-xl;
  margin-bottom: $spacing-xl;

  .format-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-sm;
    padding: $spacing-lg;
    background: rgba($white, 0.15);
    border-radius: $border-radius-lg;
    backdrop-filter: blur(12px);
    border: 1px solid rgba($white, 0.2);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      background: rgba($white, 0.25);
      box-shadow: 0 8px 25px rgba($black, 0.2);
    }

    span {
      font-weight: 600;
      font-size: 0.95rem;
      color: $white;
      text-shadow: 0 1px 3px rgba($black, 0.3);
    }

    .iconify {
      color: $white;
      filter: drop-shadow(0 2px 4px rgba($black, 0.3));
    }
  }
}

.feature-tags {
  display: flex;
  justify-content: center;
  gap: $spacing-md;
  flex-wrap: wrap;

  .feature-tag {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    background: rgba($white, 0.2);
    padding: $spacing-sm $spacing-md;
    border-radius: $border-radius-lg;
    backdrop-filter: blur(8px);
    border: 1px solid rgba($white, 0.25);
    font-size: 0.9rem;
    font-weight: 500;
    color: $white;
    text-shadow: 0 1px 2px rgba($black, 0.2);
    transition: all 0.2s ease;

    &:hover {
      background: rgba($white, 0.3);
      transform: translateY(-2px);
    }

    .iconify {
      color: rgba($white, 0.9);
      filter: drop-shadow(0 1px 2px rgba($black, 0.2));
    }
  }
}

// 背景装饰圆圈
.hero-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba($white, 0.1);
  backdrop-filter: blur(2px);

  &.decoration-1 {
    width: 200px;
    height: 200px;
    top: 10%;
    left: 85%;
    animation: float 6s ease-in-out infinite;
  }

  &.decoration-2 {
    width: 150px;
    height: 150px;
    top: 70%;
    left: 10%;
    animation: float 4s ease-in-out infinite reverse;
  }

  &.decoration-3 {
    width: 120px;
    height: 120px;
    top: 20%;
    left: 5%;
    animation: float 5s ease-in-out infinite;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(180deg);
  }
}

// 响应式设计
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .format-showcase {
    flex-direction: column;
    align-items: center;
    gap: $spacing-lg;

    .format-item {
      flex-direction: row;
      padding: $spacing-md $spacing-lg;
      width: 100%;
      max-width: 280px;
    }
  }

  .feature-tags {
    .feature-tag {
      font-size: 0.8rem;
      padding: $spacing-xs $spacing-sm;
    }
  }
}

@media (max-width: 480px) {
  .ebook-hero-section {
    padding: $spacing-xl 0;
  }

  .hero-container {
    padding: 0 $spacing-md;
  }

  .hero-title {
    font-size: 1.8rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }
}
</style>
